import { useState, useEffect, useRef } from "react";
import { Settings2, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/enhanced-button";
import { Input } from "@/components/ui/input";
import type { BillWorkflowStatusDto } from "@blinkbooks/types";
import {
  listBillWorkflowStatuses,
  createBillWorkflowStatus,
  updateBillWorkflowStatus,
  deleteBillWorkflowStatus,
} from "@/services/billWorkflowStatus.service";

const SWATCHES = ["#F59E0B", "#3B82F6", "#8B5CF6", "#6366F1", "#06B6D4", "#10B981", "#EF4444", "#6B7280"];

interface ManageBillStatusesModalProps {
  open: boolean;
  onClose: () => void;
  onChanged: (statuses: BillWorkflowStatusDto[]) => void;
}

export function ManageBillStatusesModal({ open, onClose, onChanged }: ManageBillStatusesModalProps) {
  const [statuses, setStatuses] = useState<BillWorkflowStatusDto[]>([]);
  const [newLabel, setNewLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const renameTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listBillWorkflowStatuses()
      .then(setStatuses)
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    const timers = renameTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const commit = (next: BillWorkflowStatusDto[]) => {
    setStatuses(next);
    onChanged(next);
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    const color = SWATCHES[statuses.length % SWATCHES.length];
    const created = await createBillWorkflowStatus({ label: newLabel.trim(), color });
    commit([...statuses, created]);
    setNewLabel("");
  };

  const handleRename = (id: string, label: string) => {
    commit(statuses.map(s => s.id === id ? { ...s, label } : s));
    clearTimeout(renameTimers.current[id]);
    renameTimers.current[id] = setTimeout(() => {
      updateBillWorkflowStatus(id, { label });
    }, 500);
  };

  const handleRecolor = async (id: string, color: string) => {
    commit(statuses.map(s => s.id === id ? { ...s, color } : s));
    await updateBillWorkflowStatus(id, { color });
  };

  const handleDelete = async (id: string) => {
    commit(statuses.filter(s => s.id !== id));
    await deleteBillWorkflowStatus(id);
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= statuses.length) return;
    const next = [...statuses];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    commit(next);
    await Promise.all(next.map((s, i) => updateBillWorkflowStatus(s.id, { sortOrder: i })));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Manage bill statuses
          </DialogTitle>
          <DialogDescription>
            This list applies to bills across all your companies and views.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {statuses.map((s, index) => (
                <div key={s.id} className="flex items-center gap-2 rounded-md border p-1.5">
                  <div className="flex flex-col">
                    <button
                      onClick={() => handleMove(index, -1)}
                      disabled={index === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => handleMove(index, 1)}
                      disabled={index === statuses.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {SWATCHES.map((c) => (
                      <button
                        key={c}
                        onClick={() => handleRecolor(s.id, c)}
                        className="h-4 w-4 rounded-full border"
                        style={{ backgroundColor: c, borderColor: s.color === c ? "black" : "transparent" }}
                        title={c}
                      />
                    ))}
                  </div>
                  <Input
                    value={s.label}
                    onChange={(e) => handleRename(s.id, e.target.value)}
                    className="h-7 text-sm flex-1"
                  />
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="h-7 w-7 p-0">
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Input
              placeholder="New status name…"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              className="h-8 text-sm"
            />
            <Button variant="outline" size="sm" onClick={handleAdd} disabled={!newLabel.trim()} className="h-8 gap-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
